import {
  CardData,
  CardPaymentPayload,
  CardPaymentResponse,
  EncryptedCardData,
  ICardHandler,
  OtpAuthPayload,
  PayloftPaymentPayload,
  PinAuthPayload,
} from '../types';
import axiosInstance from 'utils/axiosInstance';
import { encryptCardDetails, importBase64PublicKey } from 'utils/encryption';
import { getCardType } from 'utils/cardUtils';
import { extractApiErrorMessage } from '../apiError';

export class PayloftCardHandler implements ICardHandler {
  async encryptCardData(
    cardData: CardData,
    publicKey: string,
  ): Promise<EncryptedCardData> {
    const expMonth = cardData.expirationDate.split('/')[0];
    const expYear = cardData.expirationDate.split('/')[1];

    const key = importBase64PublicKey(publicKey);
    return encryptCardDetails(key, {
      cvv: cardData.cvv,
      number: cardData.cardNumber,
      expiryMonth: expMonth,
      expiryYear: expYear,
      pin: cardData.pin,
    }) as unknown as EncryptedCardData;
  }

  async processPayment(
    _payload: CardPaymentPayload,
  ): Promise<CardPaymentResponse> {
    throw new Error(
      'Payloft only supports /payloft/payment through PIN auth processing.',
    );
  }

  async processVisaPayment(
    _payload: CardPaymentPayload,
  ): Promise<CardPaymentResponse> {
    throw new Error('Visa payment processing is not supported by Payloft.');
  }

  async processPin(payload: PinAuthPayload) {
    if (!payload.cardData || !payload.publicKey || !payload.amount) {
      throw new Error(
        'Payloft PIN processing requires card data, public key, and amount.',
      );
    }

    const encryptedCard = await this.encryptCardData(
      { ...payload.cardData, pin: payload.pin },
      payload.publicKey,
    );

    const cardType = getCardType(payload.cardData.cardNumber);

    const payloftPayload: PayloftPaymentPayload = {
      amount: payload.amount / 100, // Convert amount to decimal format
      fee: (payload.fee ?? 0) / 100, // Convert fee to decimal format
      currency: "566",
      returnUrl: payload.returnUrl,
      callbackUrl: payload.callbackUrl,
      scheme: cardType,
      vendorId: payload.vendorId || '',
      referenceId: payload.reference,
      customerName: payload.customerName,
      customerEmail: payload.email || '',
      isJson: payload.isJson ?? 'true',
      card: encryptedCard as unknown as string,
      cardholder: payload.cardholder,
      mobile: String(payload.mobile),
    };

    try {
      const response = await axiosInstance.post('/payloft/payment', payloftPayload);
      return response.data;
    } catch (error) {
      throw new Error(extractApiErrorMessage(error));
    }
  }

  async processOtp(payload: OtpAuthPayload) {
    const response = await axiosInstance.post('/payloft/payments/authorize', {
      transactionRef: payload.reference,
      otp: payload.otp,
    });

    return response.data;
  }
}
